import NumberFlow from "@number-flow/react";
import {
  Calendar,
  Clock,
  HandCoins,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import Image from "next/image";
import { FC, useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { cn, textColorBasedOnNumber } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { getDashboardAnalytics } from "@/services/controllers/dashboard";
import { AnalyticsData, isDexieEnabled } from "@/services/db";
import { AnalyticsQueries } from "@/services/db/quries";
import { useAppDispatch, useAppSelector } from "@/services/hooks/redux.hooks";

import { Asset } from "./types";

export const DashboardAnalytics: FC<{ onlyTops?: boolean }> = ({
  onlyTops = false,
}) => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const { selectedExchange, loadingKeys } = useAppSelector(
    (state) => state.exchangeSwitcher,
  );
  const { focusedPortfolio, loaded } = useAppSelector(
    (state) => state.dashboard,
  );
  const [analytics, setAnalytics] = useState<AnalyticsData>();
  const [topGainer, setTopGainer] = useState<Asset>();
  const [topLooser, setTopLooser] = useState<Asset>();

  useEffect(() => {
    const controller = new AbortController();
    if (!loadingKeys && !onlyTops && typeof window !== "undefined") {
      getDashboardAnalytics({
        signal: controller.signal,
        params: {
          exchange_key_id: selectedExchange?.exchange_key_id,
        },
      }).then((response) => {
        // getDashboardAnalytics returns `data.data.data` — the same value the
        // Dexie read path (AnalyticsQueries) feeds to setAnalytics. Passing
        // `response` (not `response.data`) keeps both paths identical; the old
        // `.data` went one level too deep and left analytics undefined.
        if (!isDexieEnabled()) setAnalytics(response);
      });
      if (isDexieEnabled()) {
        AnalyticsQueries.getLatestAnalytics((data) => {
          setAnalytics(data);
        });
      }
    }
    return () => {
      controller.abort();
    };
  }, [dispatch, loadingKeys, onlyTops, selectedExchange, toast]);

  useEffect(() => {
    if (loaded && Array.isArray(focusedPortfolio?.assets)) {
      const _24hPriceChangeArray: number[] = (focusedPortfolio?.assets
        .map((e) => e.asset_details?.asset_data?.price_change_percentage_24h)
        .filter((e: any) => !isNaN(e)) ?? []) as number[];
      setTopGainer(
        focusedPortfolio.assets.find(
          (e) =>
            e.asset_details?.asset_data?.price_change_percentage_24h ===
            Math.max(..._24hPriceChangeArray),
        ),
      );
      setTopLooser(
        focusedPortfolio.assets.find(
          (e) =>
            e.asset_details?.asset_data?.price_change_percentage_24h ===
            Math.min(..._24hPriceChangeArray),
        ),
      );
    }
  }, [loaded, focusedPortfolio]);

  return (
    <>
      <div className="w-full space-y-4 md:hidden grid">
        {onlyTops ? (
          <Card className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-3">
              <HandCoins className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{m.total_assets()}</span>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold">
                <NumberFlow
                  value={focusedPortfolio?.assets.length as number}
                  format={{
                    style: "decimal",
                  }}
                />
              </p>
            </div>
          </Card>
        ) : (
          <></>
        )}
        {!onlyTops ? (
          <>
            <Card className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4" />
                <span className="font-medium">{m._24hr_performance()}</span>
              </div>
              <div className="text-right font-medium">
                <NumberFlow
                  value={parseFloat(analytics?.["24h_valuation"]?.value ?? "0")}
                  format={{
                    style: "currency",
                    currency: "USD",
                    currencyDisplay: "narrowSymbol",
                  }}
                />
                <div
                  className={cn(
                    "text-sm font-normal",
                    textColorBasedOnNumber(
                      parseFloat(
                        analytics?.["24h_valuation"]?.percentage ?? "0",
                      ),
                    ),
                  )}
                >
                  <NumberFlow
                    value={
                      parseFloat(
                        analytics?.["24h_valuation"]?.percentage ?? "0",
                      ) / 100
                    }
                    format={{
                      style: "percent",
                      signDisplay: "always",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    }}
                  />
                </div>
              </div>
            </Card>

            <Card className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">{m._7d_performance()}</span>
              </div>
              <div className="text-right font-medium">
                <NumberFlow
                  value={parseFloat(analytics?.["7d_valuation"]?.value ?? "0")}
                  format={{
                    style: "currency",
                    currency: "USD",
                    currencyDisplay: "narrowSymbol",
                  }}
                />
                <div
                  className={cn(
                    "text-sm font-normal",
                    textColorBasedOnNumber(
                      parseFloat(
                        analytics?.["7d_valuation"]?.percentage ?? "0",
                      ),
                    ),
                  )}
                >
                  <NumberFlow
                    value={
                      parseFloat(
                        analytics?.["7d_valuation"]?.percentage ?? "0",
                      ) / 100
                    }
                    format={{
                      style: "percent",
                      signDisplay: "always",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    }}
                  />
                </div>
              </div>
            </Card>
          </>
        ) : (
          <></>
        )}
        <Card className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-4 h-4" />
            <span className="font-medium">{m.top_gainer()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Image
              src={
                topGainer?.asset_details?.asset_data?.image ??
                "/defaultIcon.svg"
              }
              alt="icon"
              width={400}
              height={400}
              className="w-6 h-6 rounded-full"
            />
            <div className="text-right">
              <div className="font-medium">
                {topGainer?.asset_details?.asset_name ?? <span>&mdash;</span>}
              </div>
              <div className="text-xs text-success">
                <NumberFlow
                  value={
                    (topGainer?.asset_details?.asset_data
                      ?.price_change_percentage_24h ?? 0) / 100
                  }
                  format={{
                    style: "percent",
                    signDisplay: "always",
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }}
                />
              </div>
            </div>
          </div>
        </Card>
        <Card className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <TrendingDown className="w-4 h-4" />
            <span className="font-medium">{m.top_looser()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Image
              src={
                topLooser?.asset_details?.asset_data?.image ??
                "/defaultIcon.svg"
              }
              alt="icon"
              width={400}
              height={400}
              className="w-6 h-6 rounded-full"
            />
            <div className="text-right">
              <div className="font-medium">
                {topLooser?.asset_details?.asset_name ?? <span>&mdash;</span>}
              </div>
              <div className="text-xs text-destructive">
                <NumberFlow
                  value={
                    (topLooser?.asset_details?.asset_data
                      ?.price_change_percentage_24h ?? 0) / 100
                  }
                  format={{
                    style: "percent",
                    signDisplay: "always",
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="gap-2 lg:grid-cols-2 md:grid-cols-2 md:gap-6 xl:grid-cols-4 md:grid hidden">
        {!onlyTops ? (
          <>
            <Card x-chunk="dashboard-01-chunk-0">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
                <CardTitle className="text-xs font-sans font-medium text-muted-foreground">
                  {m._24hr_performance()}
                </CardTitle>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>

                  <TooltipContent
                    side="top"
                    className="flex items-center gap-4"
                  >
                    <div className="grid gap-2">
                      <p>{m.lower_left_tadpole_lend()}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </CardHeader>
              <CardContent>
                <>
                  <div className="text-2xl font-bold tracking-tight">
                    <NumberFlow
                      value={parseFloat(
                        analytics?.["24h_valuation"]?.value ?? "0",
                      )}
                      format={{
                        style: "currency",
                        currency: "USD",
                        currencyDisplay: "narrowSymbol",
                      }}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    <span
                      className={cn(
                        textColorBasedOnNumber(
                          parseFloat(
                            analytics?.["24h_valuation"]?.percentage ?? "0",
                          ),
                        ),
                        "text-xs",
                      )}
                    >
                      <NumberFlow
                        value={
                          parseFloat(
                            analytics?.["24h_valuation"]?.percentage ?? "0",
                          ) / 100
                        }
                        format={{
                          style: "percent",
                          signDisplay: "always",
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }}
                      />
                    </span>{" "}
                    {m.from_yesterday()}
                  </p>
                </>
              </CardContent>
            </Card>
            <Card x-chunk="dashboard-01-chunk-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
                <CardTitle className="text-xs font-sans font-medium text-muted-foreground">
                  {m._7d_performance()}
                </CardTitle>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>

                  <TooltipContent
                    side="top"
                    className="flex items-center gap-4"
                  >
                    <div className="grid gap-2">
                      <p>{m.helpful_tasty_toucan_grow()}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </CardHeader>
              <CardContent>
                <>
                  <div className="text-2xl font-bold ">
                    <NumberFlow
                      value={parseFloat(
                        analytics?.["7d_valuation"]?.value ?? "0",
                      )}
                      format={{
                        style: "currency",
                        currency: "USD",
                        currencyDisplay: "narrowSymbol",
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span
                      className={cn(
                        textColorBasedOnNumber(
                          parseFloat(
                            analytics?.["7d_valuation"]?.percentage ?? "0",
                          ),
                        ),
                        "text-xs",
                      )}
                    >
                      <NumberFlow
                        value={
                          parseFloat(
                            analytics?.["7d_valuation"]?.percentage ?? "0",
                          ) / 100
                        }
                        format={{
                          style: "percent",
                          signDisplay: "always",
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }}
                      />
                    </span>{" "}
                    {m.from_last_week()}
                  </p>
                </>
              </CardContent>
            </Card>
          </>
        ) : (
          <></>
        )}
        {onlyTops ? (
          <Card x-chunk="dashboard-01-chunk-2">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
              <CardTitle className="text-xs font-sans font-medium text-muted-foreground">
                {m.total_assets()}
              </CardTitle>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <HandCoins className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>

                <TooltipContent side="top" className="flex items-center gap-4">
                  <div className="grid gap-2">
                    <p>{m.jumpy_known_bear_spin()}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </CardHeader>
            <CardContent>
              <>
                <div className="flex gap-2 items-center">
                  <div className="text-2xl font-bold tracking-tight">
                    <NumberFlow
                      value={focusedPortfolio?.assets.length as number}
                      format={{
                        style: "decimal",
                      }}
                    />
                  </div>
                </div>
              </>
            </CardContent>
          </Card>
        ) : (
          <></>
        )}
        <Card x-chunk="dashboard-01-chunk-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-sans font-medium text-muted-foreground">
              {m.top_gainer()}
            </CardTitle>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.dull_helpful_iguana_arise()}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </CardHeader>
          <CardContent>
            <>
              <div className="flex gap-2 items-center ">
                <Image
                  src={
                    topGainer?.asset_details?.asset_data?.image ??
                    "/defaultIcon.svg"
                  }
                  alt="icon"
                  width={400}
                  height={400}
                  className="w-6 h-6 rounded-full"
                />
                <div className="text-2xl font-bold tracking-tight">
                  {topGainer?.asset_details?.asset_name ?? <span>&mdash;</span>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                <span
                  className={cn(
                    textColorBasedOnNumber(
                      topGainer?.asset_details?.asset_data
                        ?.price_change_percentage_24h as number,
                    ),
                    "text-xs",
                  )}
                >
                  <NumberFlow
                    value={
                      (topGainer?.asset_details?.asset_data
                        ?.price_change_percentage_24h ?? 0) / 100
                    }
                    format={{
                      style: "percent",
                      signDisplay: "always",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    }}
                  />
                </span>{" "}
                {m.from_yesterday()}
              </p>
            </>
          </CardContent>
        </Card>
        <Card x-chunk="dashboard-01-chunk-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-sans font-medium text-muted-foreground">
              {m.top_looser()}
            </CardTitle>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <TrendingDown className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.sunny_major_fly_feast()}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </CardHeader>
          <CardContent>
            <>
              <div className="flex gap-2 items-center ">
                <Image
                  src={
                    topLooser?.asset_details?.asset_data?.image ??
                    "/defaultIcon.svg"
                  }
                  alt="icon"
                  width={400}
                  height={400}
                  className="w-6 h-6 rounded-full"
                />
                <div className="text-2xl font-bold tracking-tight">
                  {topLooser?.asset_details?.asset_name ?? <span>&mdash;</span>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                <span
                  className={cn(
                    textColorBasedOnNumber(
                      topLooser?.asset_details?.asset_data
                        ?.price_change_percentage_24h as number,
                    ),
                    "text-xs",
                  )}
                >
                  <NumberFlow
                    value={
                      (topLooser?.asset_details?.asset_data
                        ?.price_change_percentage_24h ?? 0) / 100
                    }
                    format={{
                      style: "percent",
                      signDisplay: "always",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    }}
                  />
                </span>{" "}
                {m.from_yesterday()}
              </p>
            </>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
